/**
 * Move focus with Arrow keys (TV remote / keyboard). Native browsers only use Tab for focus.
 * Capture phase so this runs before scroll handlers on overflow areas.
 */

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href]:not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [role="button"]:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

/** When true, ArrowLeft at the content edge does not jump back to the sidebar — use Back to return to the rail. */
let contentNavLock = false;

/** Android WebView often delivers DPAD as keyCode-only; empty `key` skips our handler and native focus escapes to the sidebar. */
function normalizeArrowKey(e) {
  const k = e.key;
  if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'ArrowLeft' || k === 'ArrowRight') return k;
  const code = e.code;
  if (code === 'ArrowUp' || code === 'ArrowDown' || code === 'ArrowLeft' || code === 'ArrowRight') return code;
  const kc = e.keyCode ?? e.which;
  if (kc === 37) return 'ArrowLeft';
  if (kc === 38) return 'ArrowUp';
  if (kc === 39) return 'ArrowRight';
  if (kc === 40) return 'ArrowDown';
  return null;
}

/** Focus is in the main column (content + top bar): arrows must never land on the left rail — use Back for that. */
function browsingMainPaneNoRail(active) {
  return (
    active instanceof HTMLElement &&
    active.closest('.main-content') &&
    !active.closest('.sidebar') &&
    !active.closest('[role="dialog"]')
  );
}

function isVisible(el) {
  if (!(el instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  const rect = el.getBoundingClientRect();
  return rect.width >= 4 && rect.height >= 4;
}

function collectFocusables(root) {
  if (!root) return [];
  return [...root.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isVisible);
}

function focusPoolRoot(activeEl) {
  const dlg = activeEl?.closest?.('[role="dialog"]');
  if (dlg) return dlg;
  return document.body;
}

/** Poster-like surfaces for toolbar/search fallbacks (includes ROM results). */
function isBrowseTileSurface(el) {
  return !!(
    el &&
    (el.closest('.card-grid') ||
      el.closest('.media-row') ||
      el.closest('.channel-grid') ||
      el.closest('[data-zflix-rom-results]') ||
      el.closest('[data-zflix-games-grid]'))
  );
}

/** Shelves/grids — vertical arrows land on leftmost tile on the visually adjacent row (not column-aligned). */
function isPosterRowBiasSurface(el) {
  if (!(el instanceof HTMLElement)) return false;
  return !!(
    el.closest('.card-grid') ||
    el.closest('.media-row') ||
    el.closest('.channel-grid') ||
    el.closest('[data-zflix-rom-results]') ||
    el.closest('[data-zflix-games-grid]')
  );
}

/** Focus targets that count as “poster/tile/shelf” rows (excludes random toolbar chips stealing Down). */
function isPosterLikeTile(el) {
  return !!(el instanceof HTMLElement && isPosterRowBiasSurface(el));
}

/**
 * Nearest horizontal band below/above `fromEl`, then leftmost focusable in that band.
 * When moving from a poster-like tile, only poster-like targets participate for row-to-row moves
 * (header/toolbars still reachable once that filter yields nothing — caller may fall back).
 */
function leftmostOnNearestVerticalRow(fromEl, candidates, direction) {
  if (!(fromEl instanceof HTMLElement) || !candidates?.length) return null;
  const fr = fromEl.getBoundingClientRect();
  const edgeTol = 8;
  /** Tighter band reduces two shelf rows merging into one stray “leftmost” jump on TV grids. */
  const bandTol = Math.max(14, fr.height * 0.28);
  const fromPoster = isPosterLikeTile(fromEl);

  let pool = candidates.filter((el) => el !== fromEl);
  if (fromPoster) {
    const tilesOnly = pool.filter(isPosterLikeTile);
    if (tilesOnly.length) pool = tilesOnly;
  }

  const rects = [];
  for (const el of pool) {
    const r = el.getBoundingClientRect();
    rects.push({ el, r });
  }

  if (direction === 'down') {
    const below = rects.filter(({ r }) => r.top >= fr.bottom - edgeTol);
    if (!below.length) return null;
    const minTop = Math.min(...below.map(({ r }) => r.top));
    const row = below.filter(({ r }) => Math.abs(r.top - minTop) <= bandTol);
    row.sort((a, b) => a.r.left - b.r.left);
    return row[0]?.el ?? null;
  }

  const above = rects.filter(({ r }) => r.bottom <= fr.top + edgeTol);
  if (!above.length) return null;
  const maxBottom = Math.max(...above.map(({ r }) => r.bottom));
  const row = above.filter(({ r }) => Math.abs(r.bottom - maxBottom) <= bandTol);
  row.sort((a, b) => a.r.left - b.r.left);
  return row[0]?.el ?? null;
}

function pickRowAboveLeftBias(fromEl, candidates) {
  return leftmostOnNearestVerticalRow(fromEl, candidates, 'up');
}

function pickRowBelowLeftBias(fromEl, candidates) {
  return leftmostOnNearestVerticalRow(fromEl, candidates, 'down');
}

/** DOM-order shelf navigation — rects lie on TV/WebView (zoom, grid wrap). */
function listShelfSectionHosts(zone) {
  if (!(zone instanceof HTMLElement)) return [];
  let sections = [...zone.children].filter((c) => c instanceof HTMLElement);
  if (
    sections.length === 1 &&
    sections[0].querySelector('.media-row, .card-grid, .channel-grid, [data-zflix-rom-results]') &&
    !sections[0].matches(`${SPATIAL_TOOLBAR_SEL}, .media-row, .card-grid, .channel-grid`)
  ) {
    sections = [...sections[0].children].filter((c) => c instanceof HTMLElement);
  }
  return sections.filter((el) => {
    if (el.matches(SPATIAL_TOOLBAR_SEL)) return false;
    return !!(
      el.matches('[data-zflix-media-row], .card-grid, .channel-grid') ||
      el.querySelector('.media-row, .card-grid, .channel-grid, [data-zflix-rom-results]')
    );
  });
}

function leftmostFocusableInShelfSection(sectionEl) {
  if (!(sectionEl instanceof HTMLElement)) return null;
  const host =
    (sectionEl.classList.contains('media-row') ? sectionEl : null) ||
    (sectionEl.classList.contains('card-grid') ? sectionEl : null) ||
    (sectionEl.classList.contains('channel-grid') ? sectionEl : null) ||
    sectionEl.querySelector('.media-row') ||
    sectionEl.querySelector('.card-grid') ||
    sectionEl.querySelector('.channel-grid') ||
    sectionEl.querySelector('[data-zflix-rom-results]');
  if (!host) return null;
  const picks = sortReadingOrder(collectFocusables(host)).filter((n) => !n.closest(SPATIAL_TOOLBAR_SEL));
  return picks[0] || null;
}

function domShelfNavigateVertical(active, key) {
  if (!(active instanceof HTMLElement)) return null;
  if (active.closest(LIVETV_ROOT_SEL)) return null;
  if (!isPosterRowBiasSurface(active)) return null;
  if (key !== 'ArrowUp' && key !== 'ArrowDown') return null;
  const zone = active.closest('.content-area');
  if (!zone) return null;
  const hosts = listShelfSectionHosts(zone);
  if (!hosts.length) return null;
  let si = -1;
  for (let i = 0; i < hosts.length; i++) {
    if (hosts[i].contains(active)) {
      si = i;
      break;
    }
  }
  if (si < 0) return null;
  const delta = key === 'ArrowDown' ? 1 : -1;
  let j = si + delta;
  while (j >= 0 && j < hosts.length) {
    const pick = leftmostFocusableInShelfSection(hosts[j]);
    if (pick && pick !== active) return pick;
    j += delta;
  }
  return null;
}

function pickPosterRowBiasNeighbor(active, key, pool) {
  if (!(active instanceof HTMLElement)) return null;
  if (!isPosterRowBiasSurface(active)) return null;
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    const domPick = domShelfNavigateVertical(active, key);
    if (domPick && domPick !== active) return domPick;
  }
  if (key === 'ArrowUp') return pickRowAboveLeftBias(active, pool);
  if (key === 'ArrowDown') return pickRowBelowLeftBias(active, pool);
  return null;
}

function toolbarSearchAboveGridCard(fromEl, pool) {
  if (!isBrowseTileSurface(fromEl)) return null;
  const inputs = pool.filter((el) => {
    if (el.tagName !== 'INPUT') return false;
    return el.closest(SPATIAL_TOOLBAR_SEL) || el.closest(APP_HEADER_SEL);
  });
  const triggers = pool.filter((el) => el.matches?.(GLOBAL_SEARCH_TRIGGER_SEL));
  const targets = [...triggers, ...inputs];
  if (!targets.length) return null;
  return leftmostOnNearestVerticalRow(fromEl, targets, 'up');
}

function bestNeighbor(fromEl, dirKey, candidates) {
  const fromRect = fromEl.getBoundingClientRect();
  const tol = 12;
  const cx = fromRect.left + fromRect.width / 2;
  const cy = fromRect.top + fromRect.height / 2;
  /** Keep Left/Right on the same visual row (flex-wrap tabs etc.) instead of jumping down-right to grids. */
  const horizBand = Math.max(fromRect.height * 0.65, 36);
  /** Prefer targets roughly under/over for vertical moves (tabs → cards). */
  const vertBand = Math.max(fromRect.width * 0.55, 48);
  let best = null;
  let bestScore = Infinity;

  for (const el of candidates) {
    if (el === fromEl) continue;
    const r = el.getBoundingClientRect();
    const ex = r.left + r.width / 2;
    const ey = r.top + r.height / 2;
    const dx = ex - cx;
    const dy = ey - cy;
    let ok = false;
    let primary = Infinity;
    let secondary = Infinity;

    if (dirKey === 'ArrowRight') {
      ok = ex > cx + tol && Math.abs(ey - cy) <= horizBand;
      primary = ok ? dx : Infinity;
      secondary = Math.abs(dy);
    } else if (dirKey === 'ArrowLeft') {
      ok = ex < cx - tol && Math.abs(ey - cy) <= horizBand;
      primary = ok ? -dx : Infinity;
      secondary = Math.abs(dy);
    } else if (dirKey === 'ArrowDown') {
      ok = ey > cy + tol && Math.abs(ex - cx) <= vertBand;
      primary = ok ? dy : Infinity;
      secondary = Math.abs(dx);
    } else if (dirKey === 'ArrowUp') {
      ok = ey < cy - tol && Math.abs(ex - cx) <= vertBand;
      primary = ok ? -dy : Infinity;
      secondary = Math.abs(dx);
    }

    if (!ok) continue;
    const score = primary + secondary * 1.55;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

function sortReadingOrder(els) {
  return [...els].sort((a, b) => {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    return ra.top - rb.top || ra.left - rb.left;
  });
}

function isDialogScrollable(el) {
  if (!(el instanceof HTMLElement)) return false;
  const oy = window.getComputedStyle(el).overflowY;
  return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2;
}

/** Nearest scrollable ancestor inside a modal (dialog shell or nested episode list). */
function nearestDialogScroller(fromEl, dialogEl) {
  let n = fromEl instanceof HTMLElement ? fromEl : null;
  while (n && n !== dialogEl) {
    if (isDialogScrollable(n)) return n;
    n = n.parentElement;
  }
  return isDialogScrollable(dialogEl) ? dialogEl : null;
}

/** Adjust scrollTop only — never focus the dialog shell (avoids jumping to top on ArrowUp). */
function keepFocusVisibleInScroller(focusedEl) {
  const dialogEl = focusedEl?.closest?.('[role="dialog"]');
  if (!dialogEl) return;
  const scroller = nearestDialogScroller(focusedEl, dialogEl);
  if (!scroller) return;
  const sr = scroller.getBoundingClientRect();
  const er = focusedEl.getBoundingClientRect();
  const pad = 10;
  /** TVs crop the edge of the picture, so what sits under the highlight needs more room at the bottom. */
  const padBottom = 40;
  if (er.top < sr.top + pad) {
    scroller.scrollTop -= sr.top + pad - er.top;
  } else if (er.bottom > sr.bottom - padBottom) {
    scroller.scrollTop += er.bottom - sr.bottom + padBottom;
  }
}

function focusDialogNeighbor(neighbor) {
  if (!(neighbor instanceof HTMLElement)) return false;
  neighbor.focus({ preventScroll: true });
  keepFocusVisibleInScroller(neighbor);
  return true;
}

/** Modal sheets: DOM/spatial moves with preventScroll — scrollIntoView on the dialog resets to top. */
function handleDialogNavigation(dialogEl, key, activeEl) {
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return false;
  if (!(dialogEl instanceof HTMLElement) || !(activeEl instanceof HTMLElement)) return false;

  if (activeEl.tagName === 'INPUT' || activeEl.tagName === 'SELECT') {
    if (key === 'ArrowLeft' || key === 'ArrowRight') return false;
    const sorted = sortReadingOrder(collectFocusables(dialogEl).filter((el) => el !== dialogEl));
    const idx = sorted.indexOf(activeEl);
    if (key === 'ArrowDown' && idx >= 0 && idx < sorted.length - 1) {
      return focusDialogNeighbor(sorted[idx + 1]);
    }
    if (key === 'ArrowUp' && idx > 0) {
      return focusDialogNeighbor(sorted[idx - 1]);
    }
    return key === 'ArrowUp';
  }

  const pool = collectFocusables(dialogEl).filter((el) => el !== dialogEl && el.getAttribute('role') !== 'dialog');
  /** Focus is on the sheet itself (it just opened): Down starts at the top, not at whatever sits below its middle. */
  if (activeEl === dialogEl && key === 'ArrowDown' && pool.length) return focusDialogNeighbor(sortReadingOrder(pool)[0]);
  let neighbor = bestNeighbor(activeEl, key, pool.filter((el) => el !== activeEl));
  if (!neighbor && (key === 'ArrowUp' || key === 'ArrowDown')) {
    const sorted = sortReadingOrder(pool);
    const idx = sorted.indexOf(activeEl);
    if (key === 'ArrowDown' && idx >= 0 && idx < sorted.length - 1) neighbor = sorted[idx + 1];
    if (key === 'ArrowUp' && idx > 0) neighbor = sorted[idx - 1];
  }
  if (neighbor) return focusDialogNeighbor(neighbor);
  return key === 'ArrowUp' || key === 'ArrowLeft';
}

/**
 * Pill/tab strips (`[data-zflix-linear-tab-strip]`): ArrowLeft/Right must follow **DOM order**.
 * `sortReadingOrder()` uses row-major bbox sort; when chips wrap (Discover toolbar width, Live TV
 * categories) the visual grid order is Movies → Music → … on row 1 and TV Shows below — logical
 * neighbors break (Music + Left skips TV Shows).
 */
function linearStripButtonsDomOrder(strip) {
  return [...strip.querySelectorAll('button[type="button"]')].filter(isVisible);
}

const SPATIAL_TOOLBAR_SEL = '[data-zflix-spatial-toolbar="true"]';
const APP_HEADER_SEL = '[data-zflix-app-header="true"]';
const GLOBAL_SEARCH_TRIGGER_SEL = '[data-zflix-global-search-trigger="true"]';
const DISCOVER_ROOT_SEL = '[data-zflix-discover-root="true"]';
const LIVETV_ROOT_SEL = '[data-zflix-livetv-root="true"]';

/** Focus to restore when leaving the global header search row (Arrow Down). */
let zflixTvSearchRestoreFocus = null;

function stashZflixSearchRestoreFocus(fromEl) {
  if (!(fromEl instanceof HTMLElement)) return;
  if (!fromEl.closest('.main-content')) return;
  if (fromEl.closest(APP_HEADER_SEL)) return;
  zflixTvSearchRestoreFocus = fromEl;
}

function popZflixSearchRestoreFocus() {
  const el = zflixTvSearchRestoreFocus;
  zflixTvSearchRestoreFocus = null;
  if (!el || !el.isConnected || !isVisible(el)) return null;
  return el;
}

/** Expanded search field, opener button, or close control in the top app bar. */
function isGlobalHeaderSearchChrome(el) {
  if (!(el instanceof HTMLElement)) return false;
  const hdr = el.closest(APP_HEADER_SEL);
  if (!hdr) return false;
  if (el.matches(GLOBAL_SEARCH_TRIGGER_SEL)) return true;
  const hz = el.closest('[data-zflix-horizontal-controls]');
  if (!hz || !hdr.contains(hz)) return false;
  return el.tagName === 'INPUT' || el.tagName === 'BUTTON';
}

function isLiveTvBrowse(active) {
  return !!(active instanceof HTMLElement && active.closest(LIVETV_ROOT_SEL));
}

/** Row/column-aware moves on CSS grid channel tiles (spatial alone jumps diagonally). */
function liveTvChannelGridNeighbor(fromEl, key) {
  if (!fromEl?.classList?.contains('livetv-channel-tile')) return null;
  const grid = fromEl.closest('.channel-grid');
  if (!grid) return null;
  const tiles = sortReadingOrder([...grid.querySelectorAll('.livetv-channel-tile')].filter(isVisible));
  if (tiles.length < 2) return null;
  const fromRect = fromEl.getBoundingClientRect();
  const cy = fromRect.top + fromRect.height / 2;
  const cx = fromRect.left + fromRect.width / 2;
  const rowTol = Math.max(fromRect.height * 0.5, 28);

  if (key === 'ArrowRight') {
    let best = null;
    let bestDx = Infinity;
    for (const t of tiles) {
      if (t === fromEl) continue;
      const r = t.getBoundingClientRect();
      const tcy = r.top + r.height / 2;
      if (Math.abs(tcy - cy) > rowTol) continue;
      if (r.left < fromRect.right - 1) continue;
      const dx = r.left - fromRect.right;
      if (dx >= 0 && dx < bestDx) {
        bestDx = dx;
        best = t;
      }
    }
    return best;
  }
  if (key === 'ArrowLeft') {
    let best = null;
    let bestDx = Infinity;
    for (const t of tiles) {
      if (t === fromEl) continue;
      const r = t.getBoundingClientRect();
      const tcy = r.top + r.height / 2;
      if (Math.abs(tcy - cy) > rowTol) continue;
      if (r.right > fromRect.left + 1) continue;
      const dx = fromRect.left - r.right;
      if (dx >= 0 && dx < bestDx) {
        bestDx = dx;
        best = t;
      }
    }
    return best;
  }
  if (key === 'ArrowDown') return leftmostOnNearestVerticalRow(fromEl, tiles, 'down');
  if (key === 'ArrowUp') return leftmostOnNearestVerticalRow(fromEl, tiles, 'up');
  return null;
}

function liveTvGuideRowNeighbor(fromEl, key) {
  if (!fromEl?.classList?.contains('livetv-guide-row')) return null;
  const host = fromEl.parentElement;
  if (!host) return null;
  const rows = sortReadingOrder([...host.querySelectorAll('.livetv-guide-row')].filter(isVisible));
  const idx = rows.indexOf(fromEl);
  if (idx < 0) return null;
  if (key === 'ArrowDown' && idx + 1 < rows.length) return rows[idx + 1];
  if (key === 'ArrowUp' && idx > 0) return rows[idx - 1];
  return null;
}

function focusLiveTvFilterInput() {
  const root = document.querySelector(LIVETV_ROOT_SEL);
  if (!root) return false;
  const inp = root.querySelector(`${SPATIAL_TOOLBAR_SEL} [data-zflix-horizontal-controls] input:not([type="hidden"])`) ||
    root.querySelector('[data-zflix-horizontal-controls] input:not([type="hidden"])');
  if (inp && isVisible(inp)) {
    inp.focus();
    inp.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

/** Sports / News / … chips — reachable when grid spatial Left/Up exhausts channel tiles. */
function focusLiveTvCategoryStripPreferred() {
  const root = document.querySelector(LIVETV_ROOT_SEL);
  if (!root) return false;
  const strip = root.querySelector('[data-zflix-livetv-category-strip]');
  if (!strip) return false;
  const tabs = linearStripButtonsDomOrder(strip);
  const cur = strip.querySelector('button[aria-current="page"]');
  const target = (cur && isVisible(cur) ? cur : tabs[0]) || null;
  if (target) {
    try {
      target.focus({ preventScroll: true });
    } catch (_) {
      target.focus();
    }
    strip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

/** Arrow Up from Live TV tiles/rows: same row-above + left bias as poster grids (header reachable). */
function pickLiveTvArrowUpTarget(fromEl) {
  const mainContent = document.querySelector('.main-content');
  const headerBar = mainContent?.querySelector(APP_HEADER_SEL);
  let pool = mainContent ? collectFocusables(mainContent).filter((el) => !el.closest('.sidebar')) : [];
  pool = pool.filter((el) => el !== fromEl);
  if (headerBar) {
    for (const h of collectFocusables(headerBar)) {
      if (!pool.includes(h)) pool.push(h);
    }
  }
  return pickRowAboveLeftBias(fromEl, pool);
}

/**
 * Channel/guide tiles: move focus predictably; swallow arrows when stuck so nested scroll + browser
 * defaults don't eat Up/Left (categories were unreachable).
 */
function handleLiveTvGridArrowNavigation(active, key, e) {
  if (!(active instanceof HTMLElement)) return false;
  if (!isLiveTvBrowse(active)) return false;
  const isTile = active.classList.contains('livetv-channel-tile');
  const isGuide = active.classList.contains('livetv-guide-row');
  if (!isTile && !isGuide) return false;

  const arrows = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
  if (!arrows.includes(key)) return false;

  if (isTile) {
    const ln = liveTvChannelGridNeighbor(active, key);
    if (ln) {
      ln.focus();
      ln.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    if (key === 'ArrowLeft') {
      /** First column: Left opens the side menu, like every other row. */
      focusZflixNavRail();
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    if (key === 'ArrowUp') {
      if (focusLiveTvCategoryStripPreferred()) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      const upPick = pickLiveTvArrowUpTarget(active);
      if (upPick) {
        upPick.focus();
        upPick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      if (focusLiveTvFilterInput()) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
    }
    e.preventDefault();
    e.stopPropagation();
    return true;
  }

  if (isGuide) {
    const ln = liveTvGuideRowNeighbor(active, key);
    if (ln) {
      ln.focus();
      ln.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    if (key === 'ArrowUp') {
      if (focusLiveTvCategoryStripPreferred()) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      const upPick = pickLiveTvArrowUpTarget(active);
      if (upPick) {
        upPick.focus();
        upPick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      if (focusLiveTvFilterInput()) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    if (key === 'ArrowLeft') {
      focusZflixNavRail();
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  }

  return false;
}

function isDiscoverBrowse(active) {
  return !!(active instanceof HTMLElement && active.closest(DISCOVER_ROOT_SEL));
}

/** ArrowUp from Discover inline search → tab strip (do not jump to global header / sidebar rail). */
function focusDiscoverActiveTabFromToolbar(activeEl) {
  const root = activeEl.closest(DISCOVER_ROOT_SEL);
  if (!root) return false;
  const strip = root.querySelector('[data-zflix-linear-tab-strip]');
  if (!strip) return false;
  const tabs = linearStripButtonsDomOrder(strip);
  const cur = strip.querySelector('button[type="button"][aria-current="page"]');
  const target = (cur && isVisible(cur) ? cur : tabs[0]) || null;
  if (target) {
    target.focus();
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

function discoverSectionMounted() {
  return !!document.querySelector(DISCOVER_ROOT_SEL);
}

/** First focusable poster/card under Discover (explicit grid query — avoids spatial drift to the rail). */
function focusDiscoverGridFirstCard() {
  const root = document.querySelector(DISCOVER_ROOT_SEL);
  if (!root) return null;
  const wrap = root.querySelector('[data-zflix-discover-grid]');
  const grid = wrap?.querySelector('.card-grid') || root.querySelector('.card-grid') || wrap;
  if (!grid) return null;
  const picks = sortReadingOrder(collectFocusables(grid)).filter((n) => !n.closest(SPATIAL_TOOLBAR_SEL));
  return picks[0] || null;
}

function focusGlobalHeaderSearchFromInnerToolbar() {
  const header = document.querySelector(APP_HEADER_SEL);
  if (!header) return false;
  /**
   * Live TV is playing in the corner: going up to the header lands on its "Full screen / Stop" buttons first
   * (otherwise search opens and they cannot be reached with a remote). Up again from there goes on to search.
   */
  const chip = header.querySelector('[data-zflix-live-mini-chip]');
  const activeNow = document.activeElement;
  if (chip && !(activeNow instanceof HTMLElement && chip.contains(activeNow))) {
    const btn = chip.querySelector('button');
    if (btn && isVisible(btn)) {
      btn.focus();
      return true;
    }
  }
  const expanded = header.querySelector('[data-zflix-horizontal-controls] input:not([type="hidden"])');
  if (expanded && isVisible(expanded)) {
    expanded.focus();
    expanded.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  const trigger = header.querySelector(GLOBAL_SEARCH_TRIGGER_SEL);
  if (trigger && isVisible(trigger)) {
    trigger.click();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const inp = header.querySelector('[data-zflix-horizontal-controls] input:not([type="hidden"])');
        if (inp && isVisible(inp)) {
          inp.focus({ preventScroll: true });
          inp.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      });
    });
    trigger.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

/** Focus global header search and remember `fromEl` for Arrow Down restore. */
function focusGlobalHeaderSearchStashing(fromEl) {
  stashZflixSearchRestoreFocus(fromEl);
  return focusGlobalHeaderSearchFromInnerToolbar();
}

/** Jellyfin item id to focus after closing the inline / embed player (Continue watching card, etc.). */
let zflixPostPlayerJellyfinId = null;

export function setZflixPostPlayerFocusTarget(item) {
  if (!item || item.liveStreamId || item.playSessionId) {
    zflixPostPlayerJellyfinId = null;
    return;
  }
  zflixPostPlayerJellyfinId = item.id != null ? String(item.id) : null;
}

function cssAttrMatchEscape(id) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(id);
  return String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Call after `setPlayingItem(null)`; returns true if a resume card received focus. */
export function focusZflixResumeCardAfterPlayer() {
  const id = zflixPostPlayerJellyfinId;
  zflixPostPlayerJellyfinId = null;
  if (!id) return false;
  const el = document.querySelector(`[data-zflix-jellyfin-id="${cssAttrMatchEscape(id)}"]`);
  if (!el || !isVisible(el)) return false;
  try {
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  } catch {
    return false;
  }
}

function sectionPrefersMediaRowsBeforeGrid(contentArea) {
  const sec = contentArea?.getAttribute?.('data-zflix-section');
  return sec === 'movies' || sec === 'shows';
}

/**
 * @param {boolean} [browseGridOnly] — skip inline strips (e.g. ROM platform row) so Down from search hits the first card/result row.
 */
function focusFirstPrimaryFocusable(contentArea, browseGridOnly = false) {
  if (!contentArea) return null;

  if (contentArea.classList?.contains('content-area-livetv')) {
    const scroll = contentArea.querySelector('[data-zflix-livetv-scroll]');
    if (scroll) {
      const grid = scroll.querySelector('.channel-grid');
      if (grid) {
        const chTiles = sortReadingOrder([...grid.querySelectorAll('.livetv-channel-tile')].filter(isVisible));
        if (chTiles[0]) return chTiles[0];
      }
      const guideRows = sortReadingOrder([...scroll.querySelectorAll('.livetv-guide-row')].filter(isVisible));
      if (guideRows[0]) return guideRows[0];
    }
  }

  if (!browseGridOnly && sectionPrefersMediaRowsBeforeGrid(contentArea)) {
    const cwHost = contentArea.querySelector('[data-zflix-media-row="continue-watching"]');
    const cwRow = cwHost?.querySelector('.media-row');
    if (cwRow) {
      const cwPicks = sortReadingOrder(collectFocusables(cwRow)).filter((node) => !node.closest(SPATIAL_TOOLBAR_SEL));
      if (cwPicks[0]) return cwPicks[0];
    }
  }

  const preferSelectors = browseGridOnly
    ? ['.card-grid', '.media-row', '.channel-grid', '[data-zflix-rom-results]']
    : sectionPrefersMediaRowsBeforeGrid(contentArea)
      ? ['.media-row', '.card-grid', '.channel-grid', '[data-zflix-rom-results]', '[data-zflix-rom-controls]']
      : ['.card-grid', '.media-row', '.channel-grid', '[data-zflix-rom-results]', '[data-zflix-rom-controls]'];
  for (const sel of preferSelectors) {
    const host = contentArea.querySelector(sel);
    if (!host) continue;
    const picks = sortReadingOrder(collectFocusables(host)).filter((node) => !node.closest(SPATIAL_TOOLBAR_SEL));
    const pick = picks[0];
    if (pick) return pick;
  }
  const all = sortReadingOrder(collectFocusables(contentArea));
  const primary = all.filter((node) => !node.closest(SPATIAL_TOOLBAR_SEL));
  return primary[0] || all[0] || null;
}

/** Enter main pane from sidebar: lock axis-to-sidebar and focus first shelf/card (or ROM row when nothing else). */
export function focusZflixContentPrimary() {
  contentNavLock = true;
  const zone = document.querySelector('.content-area');
  /** Same landing spot every time: the first row of real content (Live TV keeps its channel-grid entry). */
  if (zone && !zone.classList.contains('content-area-livetv') && focusSectionEntry()) return;
  const first = focusFirstPrimaryFocusable(zone, false);
  if (first) {
    first.focus();
    first.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

/** Discover: prefer first poster card under [data-zflix-discover-grid], not the header search field. */
export function focusZflixDiscoverGrid() {
  contentNavLock = true;
  const root = document.querySelector(DISCOVER_ROOT_SEL);
  const wrap = root?.querySelector('[data-zflix-discover-grid]');
  /** Movies and TV use rows here, not a grid. */
  const grid = wrap?.querySelector('.card-grid') || wrap;
  if (grid) {
    const picks = sortReadingOrder(collectFocusables(grid)).filter((node) => !node.closest(SPATIAL_TOOLBAR_SEL));
    const pick = picks[0];
    if (pick) {
      pick.focus();
      pick.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return;
    }
  }
  /** eBooks / loading: skeleton tiles are not focusable — avoid spatial fallback picking a random tab by bbox. */
  if (root) {
    const strip = root.querySelector('[data-zflix-linear-tab-strip]');
    const cur = strip?.querySelector('button[type="button"][aria-current="page"]');
    if (cur && isVisible(cur)) {
      cur.focus();
      cur.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return;
    }
  }
  focusZflixContentPrimary();
}

/** Discover → ROMs: first IGDB result row, else queued row, else IGDB input, else Discover tab strip. */
export function focusZflixRomsPrimary() {
  contentNavLock = true;
  const zone = document.querySelector('.content-area');
  if (!zone) return;
  const results = zone.querySelector('[data-zflix-rom-results]');
  const resultPick = results && sortReadingOrder(collectFocusables(results))[0];
  if (resultPick) {
    resultPick.focus();
    resultPick.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  const queue = zone.querySelector('[data-zflix-rom-queue]');
  const queuePick = queue && sortReadingOrder(collectFocusables(queue))[0];
  if (queuePick) {
    queuePick.focus();
    queuePick.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  const horiz = zone.querySelector('[data-zflix-horizontal-controls]');
  const hzEls = horiz ? sortReadingOrder(collectFocusables(horiz)) : [];
  const igdb = hzEls.find((el) => el.tagName === 'INPUT');
  if (igdb) {
    igdb.focus();
    igdb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  if (horiz) focusDiscoverLinearTabFromHorizontal(horiz);
}

/** Games hub: first tile in the games grid. */
export function focusZflixGamesGrid() {
  contentNavLock = true;
  const grid = document.querySelector('[data-zflix-games-grid]');
  const first = grid?.querySelector('button[type="button"]');
  if (first && isVisible(first)) {
    first.focus();
    first.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  focusZflixContentPrimary();
}

/** Spatial pool: never jump from main content into the sidebar via arrows (use Back → rail). ArrowUp from tiles includes the top app header so global search is reachable. */
function buildSpatialPool(active, key, root) {
  const contentArea = document.querySelector('.content-area');
  const mainContent = document.querySelector('.main-content');
  const headerBar = mainContent?.querySelector(APP_HEADER_SEL);
  /** Left/Right from the global top bar must stay in that bar — otherwise spatial Left lands on the sidebar (physically left). Back opens the rail. */
  const inAppHeader =
    !!(headerBar && active instanceof HTMLElement && headerBar.contains(active));
  if (inAppHeader && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    return collectFocusables(headerBar).filter((el) => el !== active);
  }

  let pool = collectFocusables(root).filter((el) => el !== active);
  const inContentArea = !!(contentArea && active?.closest?.('.content-area'));
  const discoverHere = isDiscoverBrowse(active);
  if (inContentArea && contentArea) {
    pool = collectFocusables(contentArea).filter((el) => el !== active);
    /** Include header targets on Arrow Up from Discover too — global search must stay reachable from every row. */
    if (key === 'ArrowUp' && headerBar) {
      for (const h of collectFocusables(headerBar)) {
        if (h !== active && !pool.includes(h)) pool.push(h);
      }
    }
  } else if (active?.closest?.('.main-content')) {
    pool = pool.filter((el) => !el.closest('.sidebar'));
  }
  if (browsingMainPaneNoRail(active)) {
    pool = pool.filter((el) => !el.closest('.sidebar'));
  }
  const toolbarInputOnly =
    key !== 'ArrowUp' || !!active?.closest?.('[data-zflix-spatial-toolbar]');
  if (toolbarInputOnly) {
    pool = pool.filter((el) => !(el.tagName === 'INPUT' && el.closest('[data-zflix-spatial-toolbar]')));
  }
  if (contentNavLock || discoverSectionMounted()) {
    pool = pool.filter((el) => !el.closest('.sidebar'));
  }
  return pool;
}

function handleSidebar(sidebar, key, activeEl) {
  const btns = sortReadingOrder(collectFocusables(sidebar));
  if (!btns.length) return false;
  const idx = btns.indexOf(activeEl);

  if (key === 'ArrowDown') {
    const next = btns[idx >= 0 ? idx + 1 : 0];
    if (next) {
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    return false;
  }
  if (key === 'ArrowUp') {
    const prev = idx > 0 ? btns[idx - 1] : null;
    if (prev) {
      prev.focus({ preventScroll: true });
      prev.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    /** Stay on the rail — posters/header are reached only after Select (Enter) on a category. */
    return true;
  }
  if (key === 'ArrowRight') {
    /** Right enters the page: the current section resumes where you were; any other section is opened first. */
    const isSectionBtn = activeEl?.matches?.('nav button, [data-zflix-nav-id]');
    if (isSectionBtn && activeEl.getAttribute('aria-current') !== 'page') {
      activeEl.click();
      return true;
    }
    if (isSectionBtn) focusZflixLastContent();
    return true;
  }
  return false;
}

function focusDiscoverLinearTabFromHorizontal(container) {
  const ca = container?.closest?.('.content-area');
  const strip = ca?.querySelector('[data-zflix-linear-tab-strip]');
  if (!strip) return false;
  const tabs = linearStripButtonsDomOrder(strip);
  const activeTab = strip.querySelector('button[type="button"][aria-current="page"]');
  const target = (activeTab && isVisible(activeTab) ? activeTab : tabs[0]) || null;
  if (target) {
    target.focus();
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

/** ROM row / similar: predictable Left/Right between inline controls; Down jumps to first result/card grid. */
function handleHorizontalControlsStrip(container, key, activeEl) {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(key)) return false;

  if (key === 'ArrowUp') {
    const root = focusPoolRoot(activeEl);
    const pool = buildSpatialPool(activeEl, key, root);
    const pick = pickRowAboveLeftBias(activeEl, pool);
    if (pick) {
      pick.focus();
      pick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    if (focusDiscoverLinearTabFromHorizontal(container)) return true;
    if (!container.closest('.content-area')) return true;
    return false;
  }

  /** Down must run even when `activeEl` is missing from `els` (WebView quirks); never let spatial nav steal to the rail on Discover. */
  if (key === 'ArrowDown') {
    const zone = document.querySelector('.content-area');
    const onDiscover = discoverSectionMounted();
    let card = onDiscover ? focusDiscoverGridFirstCard() : null;
    if (!card) card = focusFirstPrimaryFocusable(zone, true);
    if (card && card !== activeEl) {
      card.focus();
      card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    if (onDiscover && browsingMainPaneNoRail(activeEl)) {
      return true;
    }
    return false;
  }

  const els = sortReadingOrder(collectFocusables(container));
  const idx = els.indexOf(activeEl);
  if (idx < 0) return false;
  if (key === 'ArrowRight') {
    if (idx + 1 >= els.length) return false;
    els[idx + 1].focus();
    els[idx + 1].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  if (key === 'ArrowLeft') {
    if (idx <= 0) {
      if (focusDiscoverLinearTabFromHorizontal(container)) return true;
      /** Global header search row has no in-row target to the left — swallow so D-pad never escapes to the sidebar. */
      if (!container.closest('.content-area')) return true;
      return false;
    }
    els[idx - 1].focus();
    els[idx - 1].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }
  return false;
}

/** Wrapped pill strips (Discover tabs, Movies sort): Left/Right follow reading order, not spatial diagonals. */
function handleLinearTabStrip(container, key, activeEl) {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp'].includes(key)) return false;
  const tabs = linearStripButtonsDomOrder(container);
  const idx = tabs.indexOf(activeEl);
  if (idx < 0) return false;
  if (key === 'ArrowUp') {
    const root = focusPoolRoot(activeEl);
    const pool = buildSpatialPool(activeEl, key, root);
    const pick = pickRowAboveLeftBias(activeEl, pool);
    if (pick) {
      pick.focus();
      pick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    return false;
  }
  if (key === 'ArrowRight') {
    if (idx + 1 >= tabs.length) {
      const toolbar = container.closest(SPATIAL_TOOLBAR_SEL);
      const inp = toolbar?.querySelector('input:not([type="hidden"])');
      if (inp && isVisible(inp)) {
        inp.focus();
        inp.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        return true;
      }
      return true;
    }
    const next = tabs[idx + 1];
    next.focus();
    next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    try {
      next.closest('[data-zflix-linear-tab-strip]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    } catch (_) {}
    return true;
  }
  if (idx <= 0) return true;
  const prev = tabs[idx - 1];
  prev.focus();
  prev.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  try {
    prev.closest('[data-zflix-linear-tab-strip]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  } catch (_) {}
  return true;
}

function focusSidebarActive() {
  const sidebar = document.querySelector('.sidebar');
  if (!sidebar || window.getComputedStyle(sidebar).display === 'none') return false;
  const activeBtn = sidebar.querySelector('nav button[aria-current="page"]');
  const target = activeBtn || sidebar.querySelector('nav button');
  if (target && isVisible(target)) {
    target.focus();
    return true;
  }
  return false;
}

/** Focus left sidebar page nav (desktop) or active bottom-nav tab (mobile) — call after closing modals / BACK. */
export function focusZflixNavRail() {
  contentNavLock = false;
  if (focusSidebarActive()) return true;
  const bn = document.querySelector('.bottom-nav');
  if (!bn || !bottomNavVisible()) return false;
  const activeBtn = bn.querySelector('button.bottom-nav-item.active') || bn.querySelector('button.bottom-nav-item');
  if (activeBtn && isVisible(activeBtn)) {
    activeBtn.focus();
    return true;
  }
  return false;
}

/** First load / refresh: land on Home in the rail (desktop sidebar or mobile bottom nav). */
export function focusZflixNavHome() {
  contentNavLock = false;
  const sidebar = document.querySelector('.sidebar');
  if (sidebar && window.getComputedStyle(sidebar).display !== 'none') {
    const home = sidebar.querySelector('[data-zflix-nav-id="home"]');
    if (home && isVisible(home)) {
      home.focus();
      return true;
    }
  }
  const bn = document.querySelector('.bottom-nav');
  if (bn && bottomNavVisible()) {
    const home = bn.querySelector('[data-zflix-nav-id="home"]');
    if (home && isVisible(home)) {
      home.focus();
      return true;
    }
  }
  return focusZflixNavRail();
}

function bottomNavVisible() {
  const bn = document.querySelector('.bottom-nav');
  return bn && window.getComputedStyle(bn).display !== 'none';
}

function handleBottomNav(navEl, key, activeEl) {
  const items = [...navEl.querySelectorAll('button.bottom-nav-item')].filter(isVisible);
  if (!items.length) return false;
  const idx = Math.max(0, items.indexOf(activeEl));

  if (key === 'ArrowLeft') {
    items[(idx - 1 + items.length) % items.length].focus();
    return true;
  }
  if (key === 'ArrowRight') {
    items[(idx + 1) % items.length].focus();
    return true;
  }
  if (key === 'ArrowUp') {
    contentNavLock = true;
    const mainContent = document.querySelector('.main-content');
    const headerBar = mainContent?.querySelector(APP_HEADER_SEL);
    let pool = mainContent ? collectFocusables(mainContent).filter((el) => !el.closest('.sidebar')) : [];
    pool = pool.filter((el) => el !== activeEl);
    if (headerBar) {
      for (const h of collectFocusables(headerBar)) {
        if (!pool.includes(h)) pool.push(h);
      }
    }
    const pick = pickRowAboveLeftBias(activeEl, pool);
    if (pick) {
      pick.focus();
      pick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
    const content = document.querySelector('.content-area');
    const primary = focusFirstPrimaryFocusable(content, false);
    if (primary) {
      primary.focus();
      primary.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return true;
    }
  }
  return false;
}

function handleMainContentFieldNav(active, key) {
  const horiz = active?.closest?.('[data-zflix-horizontal-controls]');
  if (horiz && handleHorizontalControlsStrip(horiz, key, active)) return true;

  if (key === 'ArrowUp') {
    const root = focusPoolRoot(active);
    const pool = buildSpatialPool(active, key, root);
    const pick = pickRowAboveLeftBias(active, pool);
    if (pick && pick !== active) {
      pick.focus();
      pick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
  }

  if (key === 'ArrowDown') {
    const zone = document.querySelector('.content-area');
    const card = focusFirstPrimaryFocusable(zone, true);
    if (card && card !== active) {
      card.focus();
      card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }
  }
  return false;
}

/* ------------------------------------------------------------------------------------------------
 * Layered ("Netflix-style") navigation for the main browse surface.
 *
 * Screen model: side menu on the left; on the right a stack of horizontal rows — top bar, then
 * tabs/filters, then shelves and grid rows. Left/Right move within a row, Up/Down move between rows.
 * Left from the first item of any row opens the side menu; Right from the side menu enters the page.
 * Shelves remember the tile you were on. Dialogs, the player, Live TV tiles and the mobile bottom nav
 * keep their own handlers below.
 * ---------------------------------------------------------------------------------------------- */

const ROW_STRIP_SEL = '.media-row, [data-zflix-linear-tab-strip], [data-zflix-horizontal-controls]';
const GRID_HOST_SEL = '.card-grid, .channel-grid, [data-zflix-games-grid], [data-zflix-rom-results]';
const TOOLBARISH_SEL = `${SPATIAL_TOOLBAR_SEL}, [data-zflix-linear-tab-strip], [data-zflix-horizontal-controls]`;
const OVERLAY_SEL = '[role="dialog"], [data-zflix-player], [data-zflix-game-player], [data-zflix-photos-viewer]';

/**
 * Shelf → { el, index } last focused in it. Keyed by the shelf's name when it has one, because shelves such as
 * Continue Watching re-mount when their data refreshes; the index is the fallback when the tile itself is gone.
 */
const rowFocusMemory = new Map();
const rowFocusMemoryByEl = new WeakMap();
/** Last thing focused in the page (not menu, not overlays) — restored after popups and when re-entering from the menu. */
let lastContentFocus = null;
/** Where `lastContentFocus` sat ({ shelf, index }) so it can be found again after its shelf re-mounts. */
let lastContentShelfPos = null;

/** Stable id for a shelf: its declared name, else its position among the page's shelves ("#3"). */
function shelfName(shelf) {
  const named = shelf?.closest?.('[data-zflix-media-row]')?.getAttribute('data-zflix-media-row');
  if (named) return named;
  const content = shelf?.closest?.('.content-area');
  if (!content) return null;
  const i = [...content.querySelectorAll('.media-row')].indexOf(shelf);
  return i >= 0 ? `#${i}` : null;
}

function findShelfByName(name) {
  const content = document.querySelector('.content-area');
  if (!content || !name) return null;
  if (name.startsWith('#')) return content.querySelectorAll('.media-row')[Number(name.slice(1))] || null;
  const host = content.querySelector(`[data-zflix-media-row="${cssAttrMatchEscape(name)}"]`);
  return host?.matches('.media-row') ? host : host?.querySelector('.media-row') || null;
}

function shelfTiles(shelf) {
  return collectFocusables(shelf);
}

function rememberShelfFocus(shelf, el) {
  const entry = { el, index: shelfTiles(shelf).indexOf(el) };
  const name = shelfName(shelf);
  if (name) rowFocusMemory.set(`${document.querySelector('.content-area')?.getAttribute('data-zflix-section') || ''}:${name}`, entry);
  rowFocusMemoryByEl.set(shelf, entry);
  return { name, index: entry.index };
}

function recallShelfFocus(shelf, els) {
  const name = shelfName(shelf);
  const entry =
    (name && rowFocusMemory.get(`${document.querySelector('.content-area')?.getAttribute('data-zflix-section') || ''}:${name}`)) ||
    rowFocusMemoryByEl.get(shelf);
  if (!entry) return null;
  if (entry.el?.isConnected && els.includes(entry.el)) return entry.el;
  if (entry.index >= 0 && els.length) return els[Math.min(entry.index, els.length - 1)];
  return null;
}

function isBrowseSurfaceEl(el) {
  return (
    el instanceof HTMLElement &&
    !!el.closest('.main-content') &&
    !el.closest(OVERLAY_SEL) &&
    !el.closest('.sidebar') &&
    !el.closest('.bottom-nav')
  );
}

function noteBrowseFocus(t) {
  if (!isBrowseSurfaceEl(t)) return;
  const shelf = t.closest('.media-row');
  const pos = shelf ? rememberShelfFocus(shelf, t) : null;
  if (t.closest('.content-area')) {
    lastContentFocus = t;
    lastContentShelfPos = pos && pos.name ? pos : null;
  }
}

/** Mouse / touch / native focus. Arrow moves also call noteBrowseFocus directly: some TV WebViews skip focus events. */
document.addEventListener('focusin', (ev) => noteBrowseFocus(ev.target), true);

function isToolbarishEl(el) {
  return el.tagName === 'INPUT' || el.tagName === 'SELECT' || !!el.closest(TOOLBARISH_SEL);
}

function moveFocusTo(el, { toTop = false } = {}) {
  if (!(el instanceof HTMLElement)) return false;
  try {
    el.focus({ preventScroll: true });
  } catch (_) {
    el.focus();
  }
  noteBrowseFocus(el);
  if (toTop) {
    const content = el.closest('.content-area');
    if (content) content.scrollTop = 0;
  }
  /**
   * Bring the whole tile (and its focus ring — see the scroll-margin rules in index.css) into view. On TV this is
   * instant: a smooth scroll gets cut off by the next key press and leaves the selected tile half off screen.
   */
  const tv = document.documentElement.classList.contains('zflix-tv-apk-density');
  el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: tv ? 'auto' : 'smooth' });
  return true;
}

/**
 * Rows of the page, top to bottom: [{ els, top, bottom, strip, header }].
 * Strips (shelves, tab strips, inline control rows) are one row each; everything else is banded by its top edge.
 * Rows that sit on the same visual line (tabs + inline search + sort chips) are merged into one.
 */
function buildBrowseRows() {
  const main = document.querySelector('.main-content');
  if (!main) return [];
  const header = main.querySelector(APP_HEADER_SEL);
  const content = main.querySelector('.content-area');
  const rows = [];

  if (header) {
    const els = sortReadingOrder(collectFocusables(header));
    if (els.length) rows.push({ els, top: -1e6, bottom: -1e6 + 1, strip: null, header: true });
  }
  if (!content) return rows;

  const cTop = content.getBoundingClientRect().top - content.scrollTop;
  const stripRows = new Map();
  const loose = [];
  for (const el of collectFocusables(content)) {
    if (el.closest(OVERLAY_SEL)) continue;
    const strip = el.closest(ROW_STRIP_SEL);
    if (strip && content.contains(strip)) {
      let row = stripRows.get(strip);
      if (!row) {
        const sr = strip.getBoundingClientRect();
        row = { els: [], top: sr.top - cTop, bottom: sr.bottom - cTop, strip, header: false };
        stripRows.set(strip, row);
      }
      row.els.push(el);
    } else {
      const r = el.getBoundingClientRect();
      loose.push({ el, top: r.top - cTop, bottom: r.bottom - cTop });
    }
  }

  const contentRows = [...stripRows.values()];
  loose.sort((a, b) => a.top - b.top);
  let band = null;
  for (const item of loose) {
    const tol = Math.max(14, (item.bottom - item.top) * 0.28);
    if (band && Math.abs(item.top - band.top) <= tol) {
      band.els.push(item.el);
      band.bottom = Math.max(band.bottom, item.bottom);
    } else {
      band = { els: [item.el], top: item.top, bottom: item.bottom, strip: null, header: false };
      contentRows.push(band);
    }
  }

  contentRows.sort((a, b) => a.top - b.top || a.bottom - b.bottom);
  const merged = [];
  for (const row of contentRows) {
    const prev = merged[merged.length - 1];
    const isShelf = (r) => !!r.strip?.classList?.contains('media-row');
    if (prev && !isShelf(prev) && !isShelf(row)) {
      const overlap = Math.min(prev.bottom, row.bottom) - Math.max(prev.top, row.top);
      const minH = Math.max(1, Math.min(prev.bottom - prev.top, row.bottom - row.top));
      if (overlap / minH > 0.5) {
        prev.els = prev.els.concat(row.els);
        prev.top = Math.min(prev.top, row.top);
        prev.bottom = Math.max(prev.bottom, row.bottom);
        prev.strip = null;
        prev.mergedLine = true;
        continue;
      }
    }
    merged.push(row);
  }
  for (const row of merged) {
    /** Shelves keep DOM order (tiles scrolled off-screen still count); everything else reads left to right. */
    if (!row.strip?.classList?.contains('media-row')) {
      row.els.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
    }
  }
  return rows.concat(merged);
}

function nearestByCenterX(fromEl, els) {
  const fr = fromEl.getBoundingClientRect();
  const cx = fr.left + fr.width / 2;
  let best = null;
  let bestD = Infinity;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    const d = Math.abs(r.left + r.width / 2 - cx);
    if (d < bestD) {
      bestD = d;
      best = el;
    }
  }
  return best;
}

/** Which element of `row` gets focus when arriving vertically from `fromEl` (which sits in `fromRow`). */
function pickInRow(row, fromEl, fromRow) {
  if (!row?.els?.length) return null;
  if (row.strip?.classList?.contains('media-row')) {
    const remembered = recallShelfFocus(row.strip, row.els);
    if (remembered) return remembered;
  }
  const currentTab = row.els.find(
    (el) => el.getAttribute('aria-current') === 'page' && el.closest('[data-zflix-linear-tab-strip]'),
  );
  if (currentTab) return currentTab;
  const fromGrid = fromEl?.closest?.(GRID_HOST_SEL);
  if (fromGrid && fromGrid === row.els[0].closest(GRID_HOST_SEL)) return nearestByCenterX(fromEl, row.els);
  const fromIsFree = fromEl && fromRow && !fromRow.strip && !fromRow.header && !fromRow.mergedLine;
  if (fromIsFree && !row.strip && !row.header && !row.mergedLine) return nearestByCenterX(fromEl, row.els);
  return row.els[0];
}

/** First row that holds real content (hero buttons, posters, tiles) rather than tabs / filters / search. */
function findEntryTarget() {
  const rows = buildBrowseRows().filter((r) => !r.header);
  if (!rows.length) return { el: null, primary: false };
  const primaryRow = rows.find((r) => r.els.some((el) => !isToolbarishEl(el)));
  if (primaryRow) {
    /** Always the row's first item — entering a section lands in the same place every time. */
    const el = primaryRow.els.find((x) => !isToolbarishEl(x));
    return { el, primary: true, first: primaryRow === rows[0] };
  }
  return { el: pickInRow(rows[0], null, null), primary: false, first: true };
}

let entryRetryTimer = null;

/** Land on the page's first content row; while the section is still loading, keep trying briefly. */
function focusSectionEntry() {
  if (entryRetryTimer) {
    clearInterval(entryRetryTimer);
    entryRetryTimer = null;
  }
  const attempt = () => {
    const { el, primary, first } = findEntryTarget();
    if (el) moveFocusTo(el, { toTop: !!first });
    return { el, primary };
  };
  const firstTry = attempt();
  let placed = firstTry.el;
  let primaryPlaced = firstTry.primary;
  let tries = 0;
  /** Sections load in stages (skeletons, then shelves): for ~5s keep the landing spot valid unless the user moves. */
  entryRetryTimer = setInterval(() => {
    tries += 1;
    const a = document.activeElement;
    const lost = !a || a === document.body || !a.isConnected;
    /** Still on the side menu only counts as "waiting" until something has been focused in the page. */
    const userMoved = !lost && a !== placed && (placed ? true : !a.closest?.('.sidebar'));
    if (userMoved || tries > 25) {
      clearInterval(entryRetryTimer);
      entryRetryTimer = null;
      return;
    }
    /** The page may still be settling (shelves loading above, a search filter clearing): follow the real first row. */
    const { el, primary, first } = findEntryTarget();
    if (el && el !== placed && (primary || lost || !placed || !primaryPlaced)) {
      moveFocusTo(el, { toTop: !!first });
      placed = el;
      primaryPlaced = primary;
    }
  }, 200);
  return !!firstTry.el;
}

/** Back to where the user was in the page (after a popup closes, or Right from the side menu). */
export function focusZflixLastContent() {
  contentNavLock = true;
  const el = lastContentFocus;
  if (el && el.isConnected && isVisible(el) && isBrowseSurfaceEl(el)) return moveFocusTo(el);
  /** The tile is gone (its shelf re-mounted): same shelf, same position. */
  if (lastContentShelfPos) {
    const shelf = findShelfByName(lastContentShelfPos.name);
    const tiles = shelf ? shelfTiles(shelf) : [];
    if (tiles.length) return moveFocusTo(tiles[Math.min(Math.max(lastContentShelfPos.index, 0), tiles.length - 1)]);
  }
  return focusSectionEntry();
}

/** A popup / viewer is open but focus is behind it: pull focus in instead of driving the page underneath. */
function pullFocusIntoOverlay(active) {
  const viewer = document.querySelector('[data-zflix-photos-viewer]');
  if (viewer) {
    if (!viewer.contains(active)) viewer.querySelector('button')?.focus({ preventScroll: true });
    return true;
  }
  const roots = document.querySelectorAll('[data-zflix-modal-root]');
  const root = roots[roots.length - 1];
  if (!root || root.contains(active)) return false;
  const dlg = root.querySelector('[role="dialog"]') || root;
  const target =
    dlg.querySelector('[data-zflix-modal-primary-action]:not([disabled])') ||
    collectFocusables(dlg).filter((el) => el !== dlg)[1] ||
    collectFocusables(dlg).filter((el) => el !== dlg)[0] ||
    dlg;
  target.focus({ preventScroll: true });
  return true;
}

function layeredNavApplies(active) {
  if (!(active instanceof HTMLElement)) return false;
  if (!isBrowseSurfaceEl(active)) return false;
  if (active.tagName === 'TEXTAREA' || active.isContentEditable || active.tagName === 'VIDEO') return false;
  if (active.classList.contains('livetv-channel-tile') || active.classList.contains('livetv-guide-row')) return false;
  return !!(active.closest(APP_HEADER_SEL) || active.closest('.content-area'));
}

/** @returns {boolean} true when the key was consumed. */
function handleLayeredNav(active, key, e) {
  /** Text fields: let Left/Right move the caret until it hits the end of the text. */
  if (active.tagName === 'INPUT' && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    let start = null;
    let end = null;
    try {
      start = active.selectionStart;
      end = active.selectionEnd;
    } catch (_) {}
    const len = (active.value || '').length;
    if (len > 0 && start != null) {
      if (key === 'ArrowLeft' && (start > 0 || end > 0)) return false;
      if (key === 'ArrowRight' && end < len) return false;
    }
  }

  const rows = buildBrowseRows();
  const ri = rows.findIndex((r) => r.els.includes(active));
  if (ri < 0) {
    const entry = findEntryTarget().el;
    if (entry) moveFocusTo(entry);
    return true;
  }
  const row = rows[ri];
  const idx = row.els.indexOf(active);

  if (key === 'ArrowRight') {
    if (idx + 1 < row.els.length) moveFocusTo(row.els[idx + 1]);
    return true;
  }
  if (key === 'ArrowLeft') {
    if (idx > 0) moveFocusTo(row.els[idx - 1]);
    else focusZflixNavRail();
    return true;
  }

  if (key === 'ArrowDown' && row.header) {
    const restored = popZflixSearchRestoreFocus();
    if (restored && isBrowseSurfaceEl(restored)) return moveFocusTo(restored);
    const back = lastContentFocus;
    if (back && back.isConnected && isVisible(back) && isBrowseSurfaceEl(back)) return moveFocusTo(back);
  }

  /**
   * Up always heads for a search box: from tabs / filter chips, the search field on that same line comes first
   * (Discover's "Search titles…"), then the top-bar search. No walking Right across every tab to reach it.
   */
  if (key === 'ArrowUp' && !row.header && active.tagName !== 'INPUT') {
    const lineSearch = row.els.find(
      (el) => el.tagName === 'INPUT' && /^(text|search|)$/i.test(el.getAttribute('type') || '') && isToolbarishEl(active),
    );
    if (lineSearch) return moveFocusTo(lineSearch);
  }

  const step = key === 'ArrowDown' ? 1 : -1;
  const target = rows[ri + step];
  if (!target) return true;
  const pick = pickInRow(target, active, row);
  if (pick) moveFocusTo(pick, { toTop: ri + step <= 1 });
  return true;
}

function handleKeydown(e) {
  /** A question on top of everything (Play on TV: "… wants to play on this TV") owns every key while it is up. */
  if (typeof window.__zflixTopKeys === 'function') {
    try {
      if (window.__zflixTopKeys(e) === true) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
    } catch (_) {}
  }
  const key = normalizeArrowKey(e);
  if (!key) return;

  /** A screen with its own column layout (Live TV) gets the key first; false means "not mine, carry on". */
  if (typeof window.__zflixOwnNav === 'function') {
    try {
      if (window.__zflixOwnNav(key, document.activeElement) === true) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    } catch (_) {}
  }

  /** The Zflix video player owns the remote while it is open (seek, pause, track menus). */
  if (document.querySelector('[data-zflix-player]:not([data-zflix-jellyfin-embed])')) return;

  const active = document.activeElement;

  if (!(active instanceof HTMLElement && active.closest(OVERLAY_SEL)) && pullFocusIntoOverlay(active)) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  /** Full-screen photo: nothing to move to — arrows must not reach the grid underneath. */
  if (active instanceof HTMLElement && active.closest('[data-zflix-photos-viewer]')) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (layeredNavApplies(active)) {
    if (handleLayeredNav(active, key, e)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  if (discoverSectionMounted() && browsingMainPaneNoRail(active)) {
    contentNavLock = true;
  }

  if (key === 'ArrowDown' && isGlobalHeaderSearchChrome(active)) {
    const restored = popZflixSearchRestoreFocus();
    if (restored) {
      restored.focus();
      restored.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  if (active?.tagName === 'INPUT' && active.closest('.main-content')) {
    e.preventDefault();
    e.stopPropagation();
    if (
      key === 'ArrowUp' &&
      isDiscoverBrowse(active) &&
      active.closest(SPATIAL_TOOLBAR_SEL) &&
      focusDiscoverActiveTabFromToolbar(active)
    ) {
      return;
    }
    if (key === 'ArrowUp' && active.closest('.content-area') && active.closest(SPATIAL_TOOLBAR_SEL) && !isDiscoverBrowse(active)) {
      const root = focusPoolRoot(active);
      const pool = buildSpatialPool(active, key, root);
      const upPick = pickRowAboveLeftBias(active, pool);
      if (upPick) {
        upPick.focus();
        upPick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        return;
      }
      if (focusGlobalHeaderSearchStashing(active)) return;
    }
    if (handleMainContentFieldNav(active, key)) return;
    if (
      key === 'ArrowDown' &&
      discoverSectionMounted() &&
      browsingMainPaneNoRail(active)
    ) {
      const pick = focusDiscoverGridFirstCard();
      if (pick) {
        pick.focus();
        pick.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        return;
      }
    }
    const root = focusPoolRoot(active);
    const pool = buildSpatialPool(active, key, root);
    let neighbor = pickPosterRowBiasNeighbor(active, key, pool);
    if (!neighbor) neighbor = bestNeighbor(active, key, pool);
    if (neighbor && neighbor.closest?.('.sidebar') && browsingMainPaneNoRail(active)) neighbor = null;
    if (neighbor) {
      neighbor.focus();
      neighbor.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return;
    }
    if (key === 'ArrowDown' && discoverSectionMounted() && browsingMainPaneNoRail(active)) {
      return;
    }
    if (key === 'ArrowLeft' && browsingMainPaneNoRail(active)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  if (active?.tagName === 'SELECT' && active.closest('.main-content')) {
    e.preventDefault();
    e.stopPropagation();
    if (handleMainContentFieldNav(active, key)) return;
    const root = focusPoolRoot(active);
    const pool = buildSpatialPool(active, key, root);
    let neighbor = bestNeighbor(active, key, pool);
    if (neighbor && neighbor.closest?.('.sidebar') && browsingMainPaneNoRail(active)) neighbor = null;
    if (neighbor) {
      neighbor.focus();
      neighbor.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return;
    }
    if (key === 'ArrowLeft' && browsingMainPaneNoRail(active)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  if (active && (active.tagName === 'TEXTAREA' || active.isContentEditable)) {
    return;
  }

  const dialogEl = active?.closest?.('[role="dialog"]');
  if (dialogEl && active instanceof HTMLElement) {
    if (handleDialogNavigation(dialogEl, key, active)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  if (handleLiveTvGridArrowNavigation(active, key, e)) {
    return;
  }

  const linearStrip = active?.closest?.('[data-zflix-linear-tab-strip]');
  if (linearStrip && active?.tagName === 'BUTTON' && handleLinearTabStrip(linearStrip, key, active)) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (linearStrip && active?.tagName === 'BUTTON' && key === 'ArrowDown') {
    contentNavLock = true;
    const zone = document.querySelector('.content-area');
    if (zone?.querySelector('[data-zflix-rom-controls]')) {
      focusZflixRomsPrimary();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const discoverRoot = linearStrip.closest(DISCOVER_ROOT_SEL);
    if (discoverRoot) {
      const toolbar = discoverRoot.querySelector(SPATIAL_TOOLBAR_SEL);
      const hz = toolbar?.querySelector('[data-zflix-horizontal-controls]');
      const inp = hz?.querySelector('input:not([type="hidden"])');
      if (inp && isVisible(inp)) {
        inp.focus();
        inp.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
    const card = focusFirstPrimaryFocusable(zone, false);
    if (card) {
      card.focus();
      card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  if (
    key === 'ArrowUp' &&
    active instanceof HTMLElement &&
    active.closest('.content-area') &&
    active.closest(SPATIAL_TOOLBAR_SEL) &&
    !isDiscoverBrowse(active) &&
    focusGlobalHeaderSearchStashing(active)
  ) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (active?.tagName === 'VIDEO') {
    const wrap = active.closest('[data-zflix-player]');
    if (wrap) {
      const ctrls = collectFocusables(wrap).filter((el) => el !== active);
      const next = ctrls[0];
      if (next) {
        next.focus();
        e.preventDefault();
        e.stopPropagation();
      }
    }
    return;
  }

  const sidebarEl = active?.closest?.('.sidebar');
  if (sidebarEl) {
    if (handleSidebar(sidebarEl, key, active)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  if (bottomNavVisible() && active?.closest?.('.bottom-nav')) {
    const bn = document.querySelector('.bottom-nav');
    if (bn && handleBottomNav(bn, key, active)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  if (
    (active === document.body || active === document.documentElement) &&
    document.querySelector('.main-content')
  ) {
    /** Focus fell to nowhere (a tile re-rendered, a popup closed): go back to where the user was. */
    if (focusZflixLastContent()) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  /** Global top bar: Left/Right = DOM order, not spatial rects (fixes inverted D-pad on TV WebViews / RTL). */
  const appHdrNav = active?.closest?.(APP_HEADER_SEL);
  if (
    appHdrNav &&
    active instanceof HTMLElement &&
    (key === 'ArrowLeft' || key === 'ArrowRight')
  ) {
    const els = sortReadingOrder(collectFocusables(appHdrNav));
    const idx = els.indexOf(active);
    if (idx >= 0) {
      e.preventDefault();
      e.stopPropagation();
      if (key === 'ArrowRight') {
        if (idx + 1 < els.length) {
          els[idx + 1].focus();
          els[idx + 1].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
        return;
      }
      if (idx > 0) {
        els[idx - 1].focus();
        els[idx - 1].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
      return;
    }
  }

  const root = focusPoolRoot(active);
  const pool = buildSpatialPool(active, key, root);
  let neighbor = pickPosterRowBiasNeighbor(active, key, pool);
  if (!neighbor) neighbor = bestNeighbor(active, key, pool);
  if (key === 'ArrowUp' && !neighbor && isBrowseTileSurface(active)) {
    neighbor = toolbarSearchAboveGridCard(active, pool);
  }

  if (neighbor && neighbor.closest?.('.sidebar') && browsingMainPaneNoRail(active)) {
    neighbor = null;
  }

  if (neighbor) {
    if (active?.closest?.('[role="dialog"]')) {
      focusDialogNeighbor(neighbor);
    } else {
      neighbor.focus();
      neighbor.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (
    key === 'ArrowDown' &&
    discoverSectionMounted() &&
    browsingMainPaneNoRail(active)
  ) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (key === 'ArrowLeft' && browsingMainPaneNoRail(active)) {
    e.preventDefault();
    e.stopPropagation();
  }

  /** WebView/TV browsers often move focus to the sidebar when spatial nav finds no neighbor — swallow Up at the chrome/content ceiling. */
  if (key === 'ArrowUp' && browsingMainPaneNoRail(active)) {
    e.preventDefault();
    e.stopPropagation();
  }
}

/**
 * MUST use `window` capture: Zflix TV APK (`MainActivity.java`) synthesizes DPAD via `window.dispatchEvent`,
 * which does not pass listeners attached only to `document.documentElement`.
 */
window.addEventListener('keydown', handleKeydown, true);
